import { QueryCache, QueryClient, queryOptions, type DefaultOptions } from "@tanstack/react-query";
import { ProblemJsonError, type PushbackClient } from "@pushback-and-chaos/client";
import { ACCOUNT_ERASURE_PENDING_CODE } from "@pushback-and-chaos/contracts";
import { retryDelay, retryTransient } from "./retry.js";

/**
 * Query defaults chosen for a product where a stale read is worse than a
 * slow one: no window-focus refetch (it would swap narration under a
 * learner's cursor), one retry (the backend has a known intermittent
 * connection failure and a single retry recovers most of them), and no
 * automatic background refetch interval anywhere.
 */
export const APP_QUERY_DEFAULTS: DefaultOptions = {
  queries: {
    refetchOnWindowFocus: false,
    retry: 1,
    staleTime: 5_000,
  },
};

/**
 * WHO THIS BROWSER IS, from the server (2026-09-23): the one `["identity"]`
 * read, shared by the root (whether a deletion is pending) and the app (who
 * is signed in). `GET /me/identity` never refuses: "nobody" is an answer.
 */
export function identityQuery(client: PushbackClient) {
  return queryOptions({
    queryKey: ["identity"],
    queryFn: () => client.getIdentity(),
    retry: retryTransient,
    retryDelay,
    staleTime: Number.POSITIVE_INFINITY,
  });
}

/**
 * Whether a failed read means the server no longer takes this browser to be
 * who the app thinks (docs/ACCOUNT-ERASURE-DESIGN-2026-10-20.md, "Web"): a
 * 401, or a pending account's 403 ACCOUNT_ERASURE_PENDING. Either way the
 * identity is asked again, and the root shows the grace notice or the app
 * follows the answer.
 */
export function rechecksIdentity(error: unknown): boolean {
  return error instanceof ProblemJsonError && (error.status === 401 || error.code === ACCOUNT_ERASURE_PENDING_CODE);
}

/**
 * The app's query client. Its cache asks `/me/identity` again whenever a read
 * fails with `rechecksIdentity`. The identity read itself never refuses, and
 * is left out so it can never set itself off; a refetch already in flight is
 * not cancelled, so a burst of refusals on one page load asks once.
 */
export function createAppQueryClient(defaultOptions: DefaultOptions = APP_QUERY_DEFAULTS): QueryClient {
  const queryCache = new QueryCache({
    onError: (error, query) => {
      if (query.queryKey[0] === "identity" || !rechecksIdentity(error)) return;
      void queryClient.invalidateQueries({ queryKey: ["identity"] }, { cancelRefetch: false });
    },
  });
  const queryClient = new QueryClient({ queryCache, defaultOptions });
  return queryClient;
}
